/**
 * PHARMAKON — Refonte Achats · Stock : paramètres d'officine, browser twin of the server CONFIG.
 *
 * The SPA reads the effective values from GET /api/v1/parametres/achats (the server merges
 * the pharmacy's `ParametresPharmacie` row with its CONFIG defaults); this module is only the
 * FALLBACK the SPA shows while that call is pending or offline (03_DATABASE DB-02, UX 02 N-14).
 *
 * Owner of the values: `apps/server/src/config/parametres-pharmacie.config.js`. This file is a
 * twin, not an import, because the API image is built from apps/server alone and cannot import
 * this workspace package (see apps/server/src/utils/telephone-dz.js). The server test
 * `src/__tests__/parametres-pharmacie.parity.test.js` imports both and fails on any drift — change
 * a default there first, then here.
 */

/** D-16 — thermal label sizes (key stored in `etiquette_format`, French label of ux/02 §6.7). */
export const ETIQUETTE_FORMATS = Object.freeze({
  "40x25": Object.freeze({ cle: "40x25", libelle: "40 × 25 mm", largeur_mm: 40, hauteur_mm: 25 }),
  "50x30": Object.freeze({ cle: "50x30", libelle: "50 × 30 mm", largeur_mm: 50, hauteur_mm: 30 }),
});

export const ETIQUETTE_FORMAT_CLES = Object.freeze(Object.keys(ETIQUETTE_FORMATS));

/**
 * D-16 / ux/02 §6.7 D10 — number of labels of one lot in one print: an integer 0…999 (« QtyInput
 * 0…999 », M-S3-21); 0 = the lot prints nothing. The SPA's QtyInput and the print-audit validator
 * take their bounds from here (browser twin + parity test).
 */
export const ETIQUETTE_COPIES = Object.freeze({ MIN: 0, MAX: 999 });

/** Cross-module X §6.5 (XD-15) — « Maximum crédit par client (DA) »: null = « Aucun plafond ». */
export const MAXIMUM_CREDIT_CLIENT_DEFAUT = null;

/**
 * CP §3.1 / D-25 (WP-62, M-19) — the two « QTE reçue » modes (keys of the Prisma enum `ModeQteRecue`,
 * French wording of the setting). A facture snapshots the mode at creation: a change applies to new
 * factures only. Twin of the server `MODES_QTE_RECUE` (parity test, which also pins the keys to the
 * Prisma enum).
 */
export const MODES_QTE_RECUE = Object.freeze({
  PAYEE_PLUS_UG: Object.freeze({
    cle: "PAYEE_PLUS_UG",
    libelle: "Qté reçue = Qté payée + UG",
    aide: "La quantité saisie est la quantité payée ; les UG s'y ajoutent pour la quantité reçue.",
  }),
  PAYEE_INCLUT_UG: Object.freeze({
    cle: "PAYEE_INCLUT_UG",
    libelle: "Qté reçue = Qté payée, UG incluses",
    aide: "La quantité saisie comprend déjà les UG ; elle est la quantité reçue.",
  }),
});

export const MODE_QTE_RECUE_CLES = Object.freeze(Object.keys(MODES_QTE_RECUE));

/** D-25 — « Qté payée + UG » is the owner's default. */
export const MODE_QTE_RECUE_DEFAUT = "PAYEE_PLUS_UG";

/**
 * D-05 / A-10, D-45 (Q4, 2026-10-17) — the CHIFA incentive rates on T.R (%): CONFIG, no longer a
 * pharmacy setting (the two columns are deprecated in place, never read); the théorique margin
 * uses these until CHIFA Bridge reports the majoration. Twin of the server `TAUX_INCENTIVE_CHIFA`.
 */
export const TAUX_INCENTIVE_CHIFA = Object.freeze({
  taux_incentive_chifa_local_pct: 20,
  taux_incentive_chifa_importe_pct: 10,
});

/** D-45 — the fields read from the CONFIG only (never from the pharmacy's row, in no section). */
export const CHAMPS_CONFIG_SEULE = Object.freeze(Object.keys(TAUX_INCENTIVE_CHIFA));

/**
 * A-06, D-09, D-05 / A-10, D-16, XD-15, D-25, D-27, D-45 — the values every screen starts from: the
 * tunable fields' defaults plus the CONFIG-only CHIFA rates; field names are the `ParametresPharmacie`
 * columns.
 */
export const PARAMETRES_PHARMACIE_DEFAUTS = Object.freeze({
  tolerance_ecart_ttc_da: 50,
  ...TAUX_INCENTIVE_CHIFA,
  etiquette_format: "40x25",
  etiquette_nom_pharmacie: true,
  etiquette_prix: true,
  maximum_credit_client_da: MAXIMUM_CREDIT_CLIENT_DEFAUT,
  mode_qte_recue: MODE_QTE_RECUE_DEFAUT,
  qte_recue_modifiable: false,
  suggestion_dernier_prix_achat: true,
});

export const PARAMETRES_PHARMACIE_CHAMPS = Object.freeze(Object.keys(PARAMETRES_PHARMACIE_DEFAUTS));

/**
 * The settings sections (XA-10): GET/PUT /api/v1/parametres/<section> reads and writes exactly
 * these fields — « achats » = « Achats et étiquettes », « caisse » = « Caisse et crédit ». The
 * CONFIG-only CHIFA rates belong to none (D-45).
 */
export const PARAMETRES_PHARMACIE_SECTIONS = Object.freeze({
  achats: Object.freeze([
    "tolerance_ecart_ttc_da",
    "etiquette_format",
    "etiquette_nom_pharmacie",
    "etiquette_prix",
    "mode_qte_recue",
    "qte_recue_modifiable",
    "suggestion_dernier_prix_achat",
  ]),
  caisse: Object.freeze(["maximum_credit_client_da"]),
});

/**
 * Bounds of the PUT bodies (from the column types, not business limits): the SPA's inputs take their
 * min / max / decimals from here (WP-20), never a typed literal. Twin of the server
 * `PARAMETRES_PHARMACIE_BORNES` (parity test).
 */
export const PARAMETRES_PHARMACIE_BORNES = Object.freeze({
  tolerance_ecart_ttc_da: Object.freeze({ min: 0, max: 99_999_999.99 }),
  maximum_credit_client_da: Object.freeze({ min: 0, max: 9_999_999_999.99 }),
  decimales: 2,
});
